// src/lib/floorMetrics.js
// STATUS: active | UPDATED: 2026-08-29
// LOG:
//   2026-08-29  New. The fourth leg of the governing test.
//
// What a manager needs to know about their floor, computed from what the app
// already records.
//
// Page 19 says a feature must help somebody learn the process, practise it,
// help a manager inspect it, or help the dealership measure and sustain it.
// Learn, practise and inspect are built. This is measure, and until now every
// table it reads had data written to it that nothing displayed.
//
// NO NEW QUERIES OF THE DATABASE'S PERMISSION MODEL
//
// RLS already grants a manager read on all five tables for their own floor:
// step_progress_manager_read, kc_attempts_manager_read, commitments_manager_read,
// activity_manager_read and signoffs_manager_select. So this file never checks a
// role. It asks for everything it can see and the database decides what that is,
// which means a salesperson calling it gets their own row and nothing else, with
// no second implementation of the rule to keep in step.
//
// FOUR NUMBERS, NOT FOURTEEN
//
// A manager opens this between customers. The questions worth answering are:
// who has gone quiet, where does the floor stall, how much is actually signed
// off, and is the coach being used at all. Anything else is a report nobody
// reads, and this repo already bans fake data for the same reason: a number
// that does not change a decision is noise wearing a suit.
//
// STUCK IS THE SHARP ONE. A step sitting at check_passed is somebody who has
// proved they know it and is waiting on a human. That is the only metric here
// that points at the manager rather than the salesperson.

import { supabase } from './supabase'
import { STEPS } from '../content/steps'

const DAY = 24 * 60 * 60 * 1000

// A week is the honest window. Shorter and a day off makes somebody look idle;
// longer and a person who quit three weeks ago still looks active.
export const QUIET_AFTER_DAYS = 7

export async function loadFloorMetrics(members) {
  const ids = (members || []).map((m) => m.id)
  if (ids.length === 0) {
    return { ok: true, empty: true }
  }

  const since = new Date(Date.now() - 7 * DAY).toISOString()

  const [progressRes, signoffRes, activityRes, coachRes, drillRes] = await Promise.all([
    supabase.from('step_progress').select('user_id, step_id, status'),
    supabase.from('manager_signoffs').select('user_id, step_id, status'),
    supabase.from('activity_events').select('user_id, kind, created_at').gte('created_at', since),
    supabase.from('activity_events').select('user_id').eq('kind', 'coach_message').gte('created_at', since),
    // Practice is the one thing a salesperson does that is entirely voluntary.
    // Nobody is made to run a drill, so it is the honest read on who is
    // actually engaged rather than who is being chased.
    supabase
      .from('activity_events')
      .select('user_id, metadata')
      .eq('kind', 'practice_drill')
      .gte('created_at', since),
  ])

  // A read that failed is not a zero. Saying "nobody did anything this week"
  // when the query errored is the kind of quiet wrong answer this codebase has
  // already been bitten by three times.
  const failed = [progressRes, signoffRes, activityRes, coachRes, drillRes].filter((r) => r.error)
  if (failed.length > 0) {
    console.error('floorMetrics: read failed', failed.map((f) => f.error.message).join(' | '))
    return { ok: false, error: 'Could not load the floor numbers.' }
  }

  return computeFloor(members, {
    progress: progressRes.data || [],
    signoffs: signoffRes.data || [],
    activity: activityRes.data || [],
    coach: coachRes.data || [],
    drills: drillRes.data || [],
  })
}

// The arithmetic, separated from the fetching so it can be tested against cases
// production does not have yet. Every one of these counts is easy to get subtly
// wrong in a way that looks plausible on screen: counting a person once per
// stuck step, counting an approved sign-off as still waiting, or reporting a
// failed read as a zero.
export function computeFloor(members, { progress, signoffs, activity, coach, drills = [] }) {
  const ids = (members || []).map((m) => m.id)
  const total = ids.length * STEPS.length

  const signedOff = progress.filter((p) => p.status === 'signed_off').length
  const passed = progress.filter((p) => p.status === 'check_passed').length

  // Waiting on a manager: passed the check, no approval yet.
  const approvedKeys = new Set(
    signoffs.filter((s) => s.status === 'approved').map((s) => `${s.user_id}:${s.step_id}`)
  )
  const waiting = progress.filter(
    (p) => p.status === 'check_passed' && !approvedKeys.has(`${p.user_id}:${p.step_id}`)
  )

  // Which step the floor stalls on. Counted across people, so one person stuck
  // on three steps does not look like three people stuck.
  const byStep = {}
  for (const w of waiting) byStep[w.step_id] = (byStep[w.step_id] || 0) + 1
  let stallStepId = null
  let stallCount = 0
  for (const [stepId, n] of Object.entries(byStep)) {
    if (n > stallCount) {
      stallCount = n
      stallStepId = stepId
    }
  }

  const activeIds = new Set(activity.map((a) => a.user_id))
  const quiet = (members || []).filter((m) => !activeIds.has(m.id))

  // Who is putting the reps in. Counted per person so a manager can name
  // somebody rather than being handed a total, because "your floor practised
  // 14 times" is a statistic and "Ion ran six drills this week" is a
  // conversation.
  const drillsByUser = {}
  for (const d of drills) drillsByUser[d.user_id] = (drillsByUser[d.user_id] || 0) + 1
  const practising = (members || [])
    .map((m) => ({ ...m, drills: drillsByUser[m.id] || 0 }))
    .filter((m) => m.drills > 0)
    .sort((a, b) => b.drills - a.drills)

  return {
    ok: true,
    empty: false,
    people: ids.length,
    totalSteps: total,
    signedOff,
    passed,
    // Percent of the floor's whole roadmap that a manager has actually signed.
    signedPct: total > 0 ? Math.round((signedOff / total) * 100) : 0,
    waiting: waiting.length,
    stallStepId,
    stallCount,
    quiet,
    coachMessages: coach.length,
    coachPeople: new Set(coach.map((c) => c.user_id)).size,
    drills: drills.length,
    practising,
    // The person who has put in the most voluntary reps this week. Worth a
    // manager saying something about, and worth nothing at all if it is faked,
    // so it is null until somebody has actually done it.
    topPractitioner: practising.length > 0 && practising[0].drills >= 2 ? practising[0] : null,
  }
}

// step_progress keys on the step uuid; the app thinks in numbers. Resolved once
// and cached, same as signoffs.js.
let numberByStepId = null

export async function resolveStepNumbers() {
  if (numberByStepId) return numberByStepId
  const { data, error } = await supabase.from('steps').select('id, number')
  if (error || !data) return {}
  numberByStepId = {}
  for (const row of data) numberByStepId[row.id] = row.number
  return numberByStepId
}

export function stepLabel(map, stepId) {
  const n = map[stepId]
  if (!n) return null
  const s = STEPS.find((x) => x.number === n)
  return s ? `Step ${n}, ${s.title}` : `Step ${n}`
}
